import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { __API_URL__ } from "../SERVER_URL";
import { UPLOAD_TYPES } from "../utils/constants";

const createExternalDocs = async ({ patientId, trialId, visitId, comment, files }) => {
  const token = localStorage.getItem("token");

  // Step 1: Get presigned URLs
  const fileMetadata = files.map((file) => ({
    name: file.name,
    type: file.type,
  }));

  const presignedRes = await axios.post(
    `${__API_URL__}/api/photos/get_presigned_urls/`,
    { files: fileMetadata, uploadType: UPLOAD_TYPES.EXTERNAL_DOCUMENTS },
    { headers: { Authorization: `Token ${token}` } }
  );

  const presignedUrls = presignedRes.data.urls;

  // Step 2: Upload files to GCS via presigned URLs
  const uploads = await Promise.all(
    presignedUrls.map(async (u, idx) => {
      const res = await fetch(u.upload_url, {
        method: "PUT",
        headers: { "Content-Type": u.content_type },
        body: files[idx],
      });
      return {
        success: res.ok,
        blob_path: u.blob_path,
        file_name: u.file_name,
      };
    })
  );

  const failed = uploads.filter((x) => !x.success);
  if (failed.length) {
    throw new Error(`${failed.length} file(s) failed to upload. Please retry.`);
  }

  // Step 3: Register documents in the backend
  await axios.post(
    `${__API_URL__}/api/patient/patient/create_additional_docs/`,
    {
      trial: trialId || "",
      comment: comment || "",
      visit: visitId || "",
      patient: patientId,
      files: presignedUrls,
    },
    { headers: { Authorization: `Token ${token}` } }
  );
};

const useCreateExternalDocsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createExternalDocs,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["external-docs"] });
      queryClient.invalidateQueries({ queryKey: ["external-docs-summary"] });
      toast.success("Documents uploaded successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to upload documents");
    },
  });
};

export default useCreateExternalDocsMutation;
